import React from 'react';
import Image from 'next/image';
import { cn } from '../../utils/cn';
import styles from './kids-loading.module.css';
import { KidsLoadingTestIDs, type KidsLoadingProps } from './kids-loading.types';

/**
 * Default KidsPlaza logo URL - circular logo from CDN
 */
const DEFAULT_LOGO_URL = 'https://static.kidsplaza.vn/assets/logos/KidsPlaza-Logo-Circle-192.png';

/**
 * KidsLoading - A branded loading spinner with KidsPlaza logo
 *
 * A customizable loading component featuring:
 * - Rotating circular animation around logo
 * - Center icon (KidsPlaza logo by default or custom)
 * - Multiple size options
 * - Optional loading text
 * - Theme color support
 *
 * Visual design matches the mobile (React Native) KidsLoading component.
 */
const KidsLoading: React.FC<KidsLoadingProps> = ({
    size: sizeProps = 'small',
    color = 'var(--color-primary-100, #21409a)',
    icon,
    text,
    className,
    containerStyle,
    testID = 'kids-loading',
}) => {
    // Calculate sizes
    const getSize = (): number => {
        if (typeof sizeProps === 'number') return sizeProps;
        return sizeProps === 'large' ? 80 : 40;
    };

    const size = getSize();
    const iconSize = size - 10;
    const circleSize = size + 50;
    const circleBorderRadius = size < 50 ? 80 : size;

    // Determine size class
    const sizeClass = sizeProps === 'large' ? styles.sizeLarge : styles.sizeSmall;

    return (
        <div
            data-testid={testID}
            className={cn(containerStyle ? undefined : styles.loadingMask, className)}
            style={containerStyle}
        >
            <div className={cn(styles.loadingContainer, sizeClass)}>
                {/* Loader Container with centered icon and spinning circle */}
                <div
                    data-testid={KidsLoadingTestIDs.loadingContainer}
                    className={styles.loaderContainer}
                    style={{ width: size, height: size }}
                >
                    {/* Center Icon/Logo */}
                    {icon ? (
                        <div
                            data-testid={KidsLoadingTestIDs.loadingImage}
                            className={styles.loadingImage}
                            style={{ width: iconSize, height: iconSize }}
                        >
                            {icon}
                        </div>
                    ) : (
                        <Image
                            data-testid={KidsLoadingTestIDs.loadingImage}
                            className={styles.loadingImage}
                            src={DEFAULT_LOGO_URL}
                            alt="KidsPlaza Loading"
                            width={iconSize}
                            height={iconSize}
                            priority
                        />
                    )}

                    {/* Spinning Circle - matches mobile style with asymmetric border */}
                    <div
                        data-testid={KidsLoadingTestIDs.spinningCircle}
                        className={styles.spinningCircle}
                        style={{
                            width: circleSize,
                            height: circleSize,
                            borderColor: color,
                            borderRadius: circleBorderRadius,
                        }}
                    />
                </div>

                {/* Loading Text */}
                {text && <div className={styles.loadingText}>{text}</div>}
            </div>
        </div>
    );
};

export default KidsLoading;
